import { supabase } from './supabase';

/** Comme COALESCE(cm.is_internal, FALSE) = FALSE dans les règles métier dashboard. */
export const NON_INTERNAL_MESSAGES_OR_FILTER =
  'is_internal.eq.false,is_internal.is.null';

export function isNonInternalMessage(message) {
  return message?.is_internal !== true && message?.isInternal !== true;
}

/** Message reçu, non interne, non lu par le lecteur courant. */
export function isMessageUnreadForViewer(message, viewerUserId) {
  if (!viewerUserId || !message) return false;
  const senderId = message.senderId ?? message.sender_id;
  if (!senderId || senderId === viewerUserId) return false;
  if (!isNonInternalMessage(message)) return false;
  return message.is_read !== true;
}

/** Messages non lus reçus par le lecteur (pas ceux qu'il a envoyés). */
export function countUnreadMessagesForViewer(messages, viewerUserId) {
  if (!viewerUserId || !messages?.length) return 0;
  return messages.filter((m) => isMessageUnreadForViewer(m, viewerUserId)).length;
}

/** Met à jour l'état local après ouverture de l'onglet Messages. */
export function markMessagesReadLocallyForViewer(messages, viewerUserId) {
  if (!viewerUserId || !messages?.length) return messages || [];
  return messages.map((m) =>
    isMessageUnreadForViewer(m, viewerUserId)
      ? { ...m, is_read: true, readBy: [viewerUserId] }
      : m
  );
}

/**
 * Compte les messages non lus par case pour le même périmètre que le KPI « Messages non lus » :
 * non lus, non internes (ou null), expéditeur différent du lecteur courant.
 */
export async function getUnreadCountsByCaseId(viewerUserId, caseIds) {
  if (!viewerUserId || !caseIds?.length) return {};

  const CHUNK = 200;
  const counts = {};

  for (let i = 0; i < caseIds.length; i += CHUNK) {
    const slice = caseIds.slice(i, i + CHUNK);
    const { data, error } = await supabase
      .from('case_messages')
      .select('case_id')
      .eq('is_read', false)
      .or(NON_INTERNAL_MESSAGES_OR_FILTER)
      .neq('sender_id', viewerUserId)
      .in('case_id', slice);

    if (error) {
      console.warn('[caseUnreadMessages] getUnreadCountsByCaseId', error);
      continue;
    }
    for (const row of data || []) {
      const id = row.case_id;
      counts[id] = (counts[id] || 0) + 1;
    }
  }

  return counts;
}

/**
 * Marque comme lus les messages reçus sur un dossier (pas ceux envoyés par le lecteur).
 */
export async function markCaseMessagesAsRead(caseId, viewerUserId) {
  if (!caseId || !viewerUserId) return { updated: 0 };

  const { data: unreadRows, error: selectError } = await supabase
    .from('case_messages')
    .select('id')
    .eq('case_id', caseId)
    .eq('is_read', false)
    .or(NON_INTERNAL_MESSAGES_OR_FILTER)
    .neq('sender_id', viewerUserId);

  if (selectError) {
    console.warn('[caseUnreadMessages] markCaseMessagesAsRead select', selectError);
    throw selectError;
  }

  const ids = (unreadRows || []).map((row) => row.id).filter(Boolean);
  if (!ids.length) return { updated: 0 };

  const { data, error } = await supabase
    .from('case_messages')
    .update({ is_read: true, read_by: viewerUserId })
    .in('id', ids)
    .select('id');

  if (!error) {
    return { updated: data?.length ?? 0 };
  }

  console.warn('[caseUnreadMessages] markCaseMessagesAsRead update with read_by', error);

  const { data: fallbackData, error: fallbackError } = await supabase
    .from('case_messages')
    .update({ is_read: true })
    .in('id', ids)
    .select('id');

  if (fallbackError) {
    console.warn('[caseUnreadMessages] markCaseMessagesAsRead fallback', fallbackError);
    throw fallbackError;
  }

  return { updated: fallbackData?.length ?? 0 };
}

export function dispatchCaseMessagesReadEvent(caseId) {
  if (typeof window === 'undefined' || !caseId) return;
  window.dispatchEvent(
    new CustomEvent('case-messages-read', { detail: { caseId } })
  );
}
